import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import * as moment from 'moment';
import { isNil, orderBy } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { cssVariables } from '@env/css-vars';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@app/shared/services/auth.service';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { EfficiencyAnalysisDatastoreService } from '../../services/efficiency-analysis-datastore.service';
import { catchError, map, takeUntil, tap, toArray } from 'rxjs/operators';
import { BuildingEfficiencyWizardStore } from '@app/efficiency-analysis/components/building-efficiency-wizard/building-efficiency-wizard.component.store';
import { GetAddressPipe } from '@app/shared/pipes/get-address.pipe';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EAChildBuildings, EABuildingDetailsAndChildren } from '@app/shared/models/efficiency-analysis/building-details-children';
import { EABuilding } from '@app/efficiency-analysis/models/buildings/efficiency-analysis-buildings';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';

@Component({
  selector: 'app-building-efficiency-details',
  templateUrl: './building-efficiency-details.component.html',
  styleUrls: ['./building-efficiency-details.component.scss'],
  providers: [LocalNumberPipe, GetAddressPipe],
})
export class BuildingEfficiencyDetailsComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() buildingsWithEA: EABuilding[] = [];
  @Input() uniqueBuildingsWithEA: EABuilding[] = [];
  @Input() selectedBuildingType: EABuilding = {} as EABuilding;
  @Output() showTree: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() isProcessDone: EventEmitter<{ isProcessDone: boolean; building: IRawWizardData }> = new EventEmitter<{
    isProcessDone: boolean;
    building: IRawWizardData;
  }>();
  @Output() reinitializeEfficiencyAnalysis: EventEmitter<void> = new EventEmitter<void>();

  public buildings: EAChildBuildings[] = [];
  public showChart = false;
  public showQuestionnaire = false;
  public selectedBuilding: EABuilding = {} as EABuilding;
  public numberOfSteps = 4;

  private selectedBuilding$: Subject<EABuilding> = new Subject<EABuilding>();

  constructor(
    public authService: AuthService,
    public matDialog: MatDialog,
    public translate: TranslateService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private buildingEfficiencyAnalysisWizardStore: BuildingEfficiencyWizardStore,
    private effAnalysisApiService: EfficiencyAnalysisApiService,
    private efficiencyAnalysisDataStore: EfficiencyAnalysisDatastoreService,
    private efficiencyAnalysisService: EfficiencyAnalysisService,
    private localNumberPipe: LocalNumberPipe,
    private overlayLoaderService: OverlayLoaderService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    this.initializeBuildingEfficiencyDetails();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onUpdateNote(building: EAChildBuildings, notes: string): void {
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    const id$: Observable<string> = building.efficiencyAnalysisGuid
      ? of(building.efficiencyAnalysisGuid)
      : this.effAnalysisApiService.getByBuildingId(building.guid, municipalityKey).pipe(
          tap((analysis: EfficiencyAnalysisDto[]) => (building.efficiencyAnalysisGuid = analysis[0].guid)),
          map((analysis: EfficiencyAnalysisDto[]): string => analysis[0].guid)
        );

    id$.pipe(takeUntil(this.unsubscribe$)).subscribe((id: string) => {
      this.effAnalysisApiService
        .saveEfficiencyAnalysis(
          {
            guid: id,
            notes: notes,
            modifyDate: moment.utc().toDate(),
            modifyUser: this.authService.currentUser.guid,
          },
          municipalityKey
        )
        .subscribe({
          next: () => {
            building.isNoteUpdateSuccess = true;
            this.buildingsWithEA.forEach((el: EABuilding) => {
              if (el.guid === building.guid) {
                el.notes = building.notes;
                return el;
              } else {
                return el;
              }
            });
          },
          error: () => (building.isNoteUpdateSuccess = false),
        });
    });
  }

  public delete(guid: string): void {
    const building: EABuilding = this.getBuildingDetails(guid);
    this.selectedBuilding$.next(building);
    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        title: 'Effizienzanalyse löschen',
        descriptionText: 'Sind Sie sicher, dass Sie die Effizienzanalyse löschen möchten? Dieser Vorgang kann nicht mehr rückgängig gemacht werden.',
        confirmText: 'Ja',
        declineText: 'Nein',
      },
      panelClass: ['-md'],
    });
    matRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.efficiencyAnalysisService.isRecalculated = true;
        this.overlayLoaderService.isLoading = true;
        const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
        this.efficiencyAnalysisDataStore
          .deleteEfficiencyAnalysis(guid, municipalityKey)
          .pipe(
            toArray(),
            catchError((error: HttpErrorResponse) => {
              this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.errorEfficiencyAnalysis, {
                objectType: building.buildingType?.name,
                data: building,
                error: error,
                errorArea: 'deleteEfficiencyAnalysis',
              });
              this.overlayLoaderService.isLoading = false;
              return throwError(() => error);
            }),
            takeUntil(this.unsubscribe$)
          )
          .subscribe(() => {
            this.overlayLoaderService.isLoading = false;
            this.efficiencyAnalysisService.isRecalculated = false;
            this.selectedBuilding$.next({} as EABuilding);
            this.reinitializeEfficiencyAnalysis.emit();

            this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.deleteEfficiencyAnalysis, {
              objectType: building.buildingType?.name,
              data: building,
              EAGuid: guid,
            });
          });
      }
    });
  }

  public openChart(guid: string): void {
    const building: EABuilding = this.getBuildingDetails(guid);
    this.selectedBuilding$.next(building);
    this.showChart = true;
    this.showTree.emit(false);
  }

  public openQuestionnaire(guid: string): void {
    const building: EABuilding = this.getBuildingDetails(guid);
    this.buildingEfficiencyAnalysisWizardStore.setBuilding(building);
    this.selectedBuilding$.next(building);
    this.showQuestionnaire = true;
    this.showTree.emit(false);
  }

  public closeQuestionnaire(wordingVisibility: boolean): void {
    if (wordingVisibility) {
      this.showQuestionnaire = false;
      this.showChart = false;
    } else {
      if (this.efficiencyAnalysisService.oldBuildingData.length > 0) {
        this.revertSelectedBuildingWizardData();
      }
      this.showQuestionnaire = false;
      this.showChart = false;
    }
  }

  public getLocalNumber(value: any): any {
    const localNumberValue = value < 1 ? this.localNumberPipe.transform(value) : this.localNumberPipe.transform(parseInt(value.toFixed(0), 10)).split(',')[0];
    return localNumberValue;
  }

  public getDate(date: any): string {
    return moment(date).format('DD.MM.YYYY');
  }

  public saveEfficiencyAnalysis(data: IRawWizardData): void {
    this.efficiencyAnalysisService.isRecalculated = true;
    this.isProcessDone.emit({ isProcessDone: true, building: data });

    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.saveSuccessText'),
        descriptionFaClass: 'fa-check-circle fa-2x',
        descriptionFaColor: cssVariables['--confirm-success-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });

    matRef.afterClosed().subscribe(() => {
      this.efficiencyAnalysisService.isRecalculated = false;
      this.reload();
      this.showTree.emit(false);
    });
  }

  private initializeBuildingEfficiencyDetails(): void {
    this.buildings = [];
    this.efficiencyAnalysisService.buildingsWithEA.forEach((building: EABuildingDetailsAndChildren) => {
      if (building.buildingTypeId === this.selectedBuildingType.buildingTypeId) {
        this.buildings = orderBy(building.children, (building: EAChildBuildings) => building.buildingName, ['asc']);
      }
    });

    this.selectedBuilding$.pipe(takeUntil(this.unsubscribe$)).subscribe((value: EABuilding) => (this.selectedBuilding = value));
  }

  private reload(): void {
    const mappedData: EABuildingDetailsAndChildren[] = this.efficiencyAnalysisService.mapBuildingData(this.buildingsWithEA, this.uniqueBuildingsWithEA);
    this.efficiencyAnalysisService.buildingsWithEA = mappedData;
    this.initializeBuildingEfficiencyDetails();
    this.showChart = false;
  }

  private getBuildingDetails(guid: string): EABuilding {
    let building: EABuilding = {} as EABuilding;
    if (!isNil(guid)) {
      building = this.buildingsWithEA.find((el: EABuilding) => el.guid === guid) as EABuilding;
    }
    return building;
  }

  private revertSelectedBuildingWizardData(): void {
    const oldData = this.efficiencyAnalysisService.oldBuildingData[0];
    const building: EABuilding =
      this.buildingsWithEA[this.buildingsWithEA.findIndex((el: EABuilding) => el.guid === this.efficiencyAnalysisService.oldBuildingData[0].guid)];

    building.floorArea = oldData.floorArea;

    building.electricityConsumption = oldData.electricityConsumption;
    building.heatingOilConsumption = oldData.heatingOilConsumption;
    building.naturalGasConsumption = oldData.naturalGasConsumption;
    building.lpgConsumption = oldData.lpgConsumption;
    building.districtHeatingConsumption = oldData.districtHeatingConsumption;
    building.electricityHeatConsumption = oldData.electricityHeatConsumption;
    building.pelletWoodConsumption = oldData.pelletWoodConsumption;

    building.electricityFromDate = oldData.electricityFromDate;
    building.heatingOilFromDate = oldData.heatingOilFromDate;
    building.naturalGasFromDate = oldData.naturalGasFromDate;
    building.lpgFromDate = oldData.lpgFromDate;
    building.districtHeatingFromDate = oldData.districtHeatingFromDate;
    building.electricityHeatFromDate = oldData.electricityHeatFromDate;
    building.pelletWoodFromDate = oldData.pelletWoodFromDate;

    building.electricityToDate = oldData.electricityToDate;
    building.heatingOilToDate = oldData.heatingOilToDate;
    building.naturalGasToDate = oldData.naturalGasToDate;
    building.lpgToDate = oldData.lpgToDate;
    building.districtHeatingToDate = oldData.districtHeatingToDate;
    building.electricityHeatToDate = oldData.electricityHeatToDate;
    building.pelletWoodToDate = oldData.pelletWoodToDate;

    building.electricityComputedValue = oldData.electricityComputedValue;
    building.heatComputedValue = oldData.heatComputedValue;
    building.electricityColorStatus = oldData.electricityColorStatus;
    building.heatColorStatus = oldData.heatColorStatus;
    building.efficiencyAnalysisDate = oldData.efficiencyAnalysisDate;
    building.efficiencyPercentageElectricity = oldData.efficiencyPercentageElectricity;
    building.efficiencyPercentageHeat = oldData.efficiencyPercentageHeat;

    this.buildingsWithEA[this.buildingsWithEA.findIndex((el: EABuilding) => el.guid === this.efficiencyAnalysisService.oldBuildingData[0].guid)] = building;
    this.selectedBuilding$.next(this.getBuildingDetails(building.guid));
  }
}
